import { type ReactNode, useEffect, useRef } from 'react';
import { Add, Close, DeleteOutline, Remove } from '@mui/icons-material';
import {
  Box,
  Button as MuiButton,
  Divider,
  Fade,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';

import { Button, theme } from '@theguarantors/tg-package-material-ui';

import {
  APPLICANT_EMAIL_UNIQUE_IGNORING_CASE,
  duplicateEmailRowIndexes,
} from '../../applicant-email.schema';
import type { ApplicantsPresentation, SectionConfig, TextFieldConfig } from '../../config';
import { collectFieldDefaults } from '../../lease-details.defaults';
import { LeaseTextInput } from '../../inputs/lease-text-input';
import { useLeaseDetailsUI } from '../../lease-details-ui.context';
import { FieldsRenderer } from '../../fields-renderer';
import { useFieldEventFormApi } from '../../fields-renderer/use-field-event-context';
import { SectionShell } from '../section-shell';

// The only array-editor section in the engine; backed by RHF `useFieldArray`.
// Two layouts, auto-selected from the config:
//  - email-only (the only field is `email`) → the compact invite UI: stacked
//    email inputs, additional rows fade in with an inline remove adornment, and
//    an outlined "add" button.
//  - otherwise (email + name fields) → the detailed UI: per-entry headers and
//    full-width fields, shape driven by field `visible`.

export type ApplicantsSectionProps = {
  config: SectionConfig & ApplicantsPresentation;
};

const APPLICANTS_FIELD = 'applicants';

type ApplicantRow = { is_deleted?: boolean; recipient_id?: string; email?: string };

const hiddenApplicantIndex = (
  softRemove: boolean | undefined,
  retainRemovedRows: boolean | undefined,
  row?: ApplicantRow,
): boolean => Boolean(softRemove && !retainRemovedRows && row?.is_deleted);

const emailOnlyRowAccess = (
  index: number,
  visiblePosition: number,
  visibleCount: number,
  config: ApplicantsPresentation,
) => {
  const lockedLeadingCount = config.lockedLeadingCount ?? 1;
  const isLocked = index < lockedLeadingCount;
  return {
    isPrimary: visiblePosition === 0,
    disabled: Boolean(config.emailsReadOnly) || index < (config.lockedLeadingCount ?? 0),
    showRemove: !config.emailsReadOnly && (Boolean(config.disableRemoveAtMin) || !isLocked),
    removeDisabled: Boolean(config.disableRemoveAtMin) && visibleCount <= config.min,
  };
};

function RemovedApplicantRow({ email, onUndo }: { email: string; onUndo: () => void }) {
  return (
    <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
      <Typography
        sx={{
          color: theme.palette.neutralGrey[600],
          textDecoration: 'line-through',
          wordBreak: 'break-word',
        }}
      >
        {email}
      </Typography>
      <Button variant="outlined" onClick={onUndo}>
        Undo
      </Button>
    </Stack>
  );
}

const defaultEntryTitle = (index: number) =>
  index === 0 ? 'Primary applicant' : `Additional applicant`;

export function ApplicantsSection({ config }: ApplicantsSectionProps) {
  const fieldKeys = Object.keys(config.fields);
  const isEmailOnly = fieldKeys.length === 1 && fieldKeys[0] === 'email';
  return isEmailOnly ? (
    <EmailOnlyApplicants config={config} />
  ) : (
    <DetailedApplicants config={config} />
  );
}

function EmailOnlyRemoveButton({
  show,
  disabled,
  icon,
  index,
  onClick,
}: {
  show: boolean;
  disabled: boolean;
  icon?: 'close' | 'delete';
  index: number;
  onClick: () => void;
}) {
  if (!show) {
    return null;
  }
  const RemoveIcon = icon === 'delete' ? DeleteOutline : Close;
  return (
    <IconButton
      aria-label={`remove applicant ${index + 1}`}
      onClick={onClick}
      disabled={disabled}
      edge="end"
      size="small"
    >
      <RemoveIcon fontSize="small" />
    </IconButton>
  );
}

function ApplicantRowFrame({ first, children }: { first: boolean; children: ReactNode }) {
  if (!first) {
    return (
      <Fade in={true}>
        <Box>{children}</Box>
      </Fade>
    );
  }
  return <Box>{children}</Box>;
}

function EmailOnlyApplicantItem({
  config,
  index,
  visiblePosition,
  activeCount,
  email,
  removed,
  required,
  labels,
  fallbackLabel,
  onBlur,
  onRemove,
  onUndo,
}: {
  config: ApplicantsSectionProps['config'];
  index: number;
  visiblePosition: number;
  activeCount: number;
  email: string;
  removed: boolean;
  required: boolean;
  labels: { primary?: string; additional?: string };
  fallbackLabel: string;
  onBlur?: (index: number, value: string | undefined) => void;
  onRemove: () => void;
  onUndo: () => void;
}) {
  const access = emailOnlyRowAccess(index, visiblePosition, activeCount, config);
  const title = (access.isPrimary ? labels.primary : labels.additional) ?? fallbackLabel;

  return (
    <ApplicantRowFrame first={visiblePosition === 0}>
      <Stack direction="column" spacing={1}>
        {removed ? (
          <RemovedApplicantRow email={email} onUndo={onUndo} />
        ) : (
          <LeaseTextInput
            name={`${APPLICANTS_FIELD}.${index}.email`}
            title={title}
            required={required}
            disabled={access.disabled}
            onBlurValue={onBlur ? (value) => onBlur(index, value) : undefined}
            endAdornment={
              <EmailOnlyRemoveButton
                show={access.showRemove}
                disabled={access.removeDisabled}
                icon={config.removeIcon}
                index={index}
                onClick={onRemove}
              />
            }
          />
        )}
        {config.renderEntryNotice?.(index)}
      </Stack>
    </ApplicantRowFrame>
  );
}

function commitEmailOnlyRemoval(
  index: number,
  row: Record<string, unknown>,
  config: ApplicantsSectionProps['config'],
  visibleCount: number,
  fieldCount: number,
  setValue: (name: string, value: boolean, options: object) => void,
  remove: (index: number) => void,
) {
  if (config.softRemove && row.recipient_id) {
    setValue(`${APPLICANTS_FIELD}.${index}.is_deleted`, true, {
      shouldDirty: true,
      shouldValidate: true,
    });
    config.onRemove?.({ count: visibleCount - 1, index });
    return;
  }
  remove(index);
  config.onRemove?.({ count: fieldCount - 1, index });
}

const listedApplicantIndexes = (
  fieldCount: number,
  rows: ApplicantRow[] | undefined,
  config: ApplicantsSectionProps['config'],
): number[] =>
  Array.from({ length: fieldCount }, (_, index) => index).filter(
    (index) => !hiddenApplicantIndex(config.softRemove, config.retainRemovedRows, rows?.[index]),
  );

const countActiveApplicants = (indexes: number[], rows: ApplicantRow[] | undefined): number =>
  indexes.reduce((count, index) => (rows?.[index]?.is_deleted ? count : count + 1), 0);

const APPLICANT_EMAIL_UNIQUE_ERROR_TYPE = 'unique-by-row';
/** Eligibility stores this so the border turns red without printing a sentence. */
const ELIGIBILITY_HIGHLIGHT_MESSAGE = '\u200b';

/**
 * Keep duplicate-email messages in sync when a row is edited or removed.
 * Only touches that error, so an empty drawer does not show "email is required".
 * Replaces an eligibility highlight so the duplicate sentence still prints.
 */
function useSyncApplicantRowErrors() {
  const { clearErrors, control, getFieldState, setError } = useFormContext();
  const rows = useWatch({ control, name: APPLICANTS_FIELD }) as ApplicantRow[] | undefined;
  const signature = (rows ?? [])
    .map((row) => `${row?.is_deleted ? 1 : 0}:${(row?.email ?? '').trim().toLowerCase()}`)
    .join('|');
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const currentRows = rows ?? [];
    const duplicates = new Set(duplicateEmailRowIndexes(currentRows));
    currentRows.forEach((_, index) => {
      const path = `${APPLICANTS_FIELD}.${index}.email`;
      const error = getFieldState(path).error;
      const errorType = error?.type;
      if (duplicates.has(index)) {
        const message = error?.message?.toString();
        if (!errorType || message === ELIGIBILITY_HIGHLIGHT_MESSAGE) {
          setError(path, {
            type: APPLICANT_EMAIL_UNIQUE_ERROR_TYPE,
            message: APPLICANT_EMAIL_UNIQUE_IGNORING_CASE,
          });
        }
        return;
      }
      if (errorType === APPLICANT_EMAIL_UNIQUE_ERROR_TYPE) {
        clearErrors(path);
      }
    });
  }, [signature, rows, clearErrors, getFieldState, setError]);
}

function useEmailOnlyApplicantActions(
  config: ApplicantsSectionProps['config'],
  activeCount: number,
  fieldCount: number,
  remove: (index: number) => void,
) {
  const { getValues, getFieldState, setValue } = useFormContext();
  const form = useFieldEventFormApi();
  const onBlur = (config.fields.email as TextFieldConfig | undefined)?.events?.onBlur;

  const fireEmailBlur = (index: number, value: string | undefined) => {
    if (!onBlur) {
      return;
    }
    const path = `${APPLICANTS_FIELD}.${index}.email`;
    onBlur({
      value: value ?? '',
      valid: !getFieldState(path).invalid,
      fieldPath: path,
      arrayIndex: index,
      sectionKey: 'applicants',
      getValues: form.getValues,
      form,
    });
  };

  const handleRemove = (index: number) => {
    const row = (getValues(`${APPLICANTS_FIELD}.${index}`) ?? {}) as Record<string, unknown>;
    commitEmailOnlyRemoval(index, row, config, activeCount, fieldCount, setValue, remove);
  };

  const handleUndo = (index: number) => {
    setValue(`${APPLICANTS_FIELD}.${index}.is_deleted`, false, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  return { onBlur: onBlur ? fireEmailBlur : undefined, handleRemove, handleUndo };
}

// Compact invite layout: email-only rows, inline remove adornment, outlined add.
function EmailOnlyApplicants({ config }: ApplicantsSectionProps) {
  useSyncApplicantRowErrors();
  const { control } = useFormContext();
  const { fields, append, remove } = useFieldArray({ control, name: APPLICANTS_FIELD });
  const rows = useWatch({ control, name: APPLICANTS_FIELD }) as ApplicantRow[] | undefined;

  const emailField = config.fields.email as TextFieldConfig | undefined;
  const required = Boolean(emailField?.required);
  const labels = config.compactEmailLabels ?? {};
  const fallbackLabel = typeof emailField?.label === 'string' ? emailField.label : '';
  const visibleIndexes = listedApplicantIndexes(fields.length, rows, config);
  const activeCount = countActiveApplicants(visibleIndexes, rows);
  const { onBlur, handleRemove, handleUndo } = useEmailOnlyApplicantActions(
    config,
    activeCount,
    fields.length,
    remove,
  );

  const handleAdd = () => {
    const index = fields.length;
    append({ ...config.defaultEntry, ...collectFieldDefaults(config.fields) });
    config.onAdd?.({ count: activeCount + 1, index });
  };

  const showAdd = !config.emailsReadOnly && activeCount < config.max;

  return (
    <SectionShell
      title={config.title}
      subtitle={config.subtitle}
      slots={config.slots}
      spacing={config.spacing}
    >
      <Stack direction="column" spacing={2}>
        {visibleIndexes.map((index, visiblePosition) => (
          <EmailOnlyApplicantItem
            key={fields[index]?.id ?? index}
            config={config}
            index={index}
            visiblePosition={visiblePosition}
            activeCount={activeCount}
            email={rows?.[index]?.email ?? ''}
            removed={Boolean(rows?.[index]?.is_deleted)}
            required={required}
            labels={labels}
            fallbackLabel={fallbackLabel}
            onBlur={onBlur}
            onRemove={() => handleRemove(index)}
            onUndo={() => handleUndo(index)}
          />
        ))}
        {showAdd ? (
          <MuiButton
            id="add-roommate-button"
            data-testid="add-roommate-button"
            variant="outlined"
            onClick={handleAdd}
            sx={{
              // `mt` omitted vs the legacy button — the enclosing Stack already
              // provides the 16px gap (legacy used `mt: 2` in a non-Stack layout).
              alignSelf: 'flex-start',
              padding: '12px',
              pl: 2,
              pr: 3,
              gap: 1,
              borderColor: `${theme.palette.neutralGrey?.[200] ?? theme.palette.grey[200]} !important`,
              [theme.breakpoints.down('md')]: { width: 'fit-content' },
            }}
          >
            <Add />
            {config.addButtonLabel ?? 'Add applicant'}
          </MuiButton>
        ) : null}
      </Stack>
    </SectionShell>
  );
}

function primaryApplicantLock(config: ApplicantsPresentation) {
  const primaryLockSpec = config.primaryLocked;
  const locksAllFields = primaryLockSpec === true;
  const lockedFieldNames =
    primaryLockSpec && typeof primaryLockSpec === 'object'
      ? new Set(primaryLockSpec.fields)
      : undefined;
  return {
    locksAllFields,
    lockedFieldNames,
    hasAnyLock: locksAllFields || (lockedFieldNames?.size ?? 0) > 0,
  };
}

// Detailed layout: per-entry headers + full-width fields (email + names).
function DetailedApplicants({ config }: ApplicantsSectionProps) {
  useSyncApplicantRowErrors();
  const { control } = useFormContext();
  const { fields, append, remove } = useFieldArray({ control, name: APPLICANTS_FIELD });
  const { applicantsSpacing } = useLeaseDetailsUI();

  const entryTitle = config.entryTitle ?? defaultEntryTitle;
  const canAdd = fields.length < config.max;
  const canRemove = (config.allowRemove ?? true) && fields.length > config.min;
  const primaryLock = primaryApplicantLock(config);

  const handleAdd = () => {
    const index = fields.length;
    append({ ...config.defaultEntry, ...collectFieldDefaults(config.fields) });
    config.onAdd?.({ count: index + 1, index });
  };

  const handleRemove = (index: number) => {
    const count = fields.length - 1;
    remove(index);
    config.onRemove?.({ count, index });
  };

  const entriesSpacing = applicantsSpacing?.entries ?? 3;
  const entrySpacing = applicantsSpacing?.entry ?? 2;

  return (
    <SectionShell
      title={config.title}
      subtitle={config.subtitle}
      slots={config.slots}
      spacing={config.spacing}
    >
      <Stack direction="column" spacing={entriesSpacing}>
        {fields.map((field, index) => {
          const isPrimary = index === 0;
          const showRemove = !isPrimary && canRemove;
          const inheritedDisabled = isPrimary && primaryLock.locksAllFields;
          const inheritedDisabledFieldNames =
            isPrimary && primaryLock.lockedFieldNames ? primaryLock.lockedFieldNames : undefined;
          const showLockedSubtitle = isPrimary && primaryLock.hasAnyLock;
          return (
            <Box key={field.id}>
              {index > 0 && <Divider sx={{ mb: entriesSpacing }} />}
              <Stack direction="column" spacing={entrySpacing}>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Typography
                    variant="body2"
                    component="h3"
                    sx={{
                      color: theme.palette.neutralGrey[950],
                      fontSize: '14px',
                      fontWeight: 400,
                      lineHeight: '22px',
                    }}
                  >
                    {entryTitle(index)}
                  </Typography>
                  {showRemove ? (
                    <MuiButton
                      aria-label={`remove applicant ${index + 1}`}
                      id={`applicants-${index}-remove`}
                      onClick={() => handleRemove(index)}
                      type="button"
                      variant="text"
                      size="small"
                      startIcon={<Remove />}
                      sx={{
                        color: theme.palette.red[600],
                        textTransform: 'none',
                        fontWeight: 400,
                        fontSize: '14px',
                        lineHeight: '22px',
                        height: '32px',
                        p: '5px 12px 5px 8px',
                        m: `0 !important`,
                      }}
                    >
                      Remove
                    </MuiButton>
                  ) : null}
                </Stack>
                <FieldsRenderer
                  fields={config.fields}
                  sectionKey="applicants"
                  arrayIndex={index}
                  inheritedDisabled={inheritedDisabled}
                  inheritedDisabledFieldNames={inheritedDisabledFieldNames}
                />
                {showLockedSubtitle && config.primaryLockedSubtitle ? (
                  <Typography variant="body2" sx={{ color: 'grey.600' }}>
                    {config.primaryLockedSubtitle}
                  </Typography>
                ) : null}
              </Stack>
            </Box>
          );
        })}
        {canAdd ? (
          <Button
            id="applicants-add"
            startIcon={<Add />}
            onClick={handleAdd}
            variant="text"
            size="medium"
            color="primary"
            sx={{ alignSelf: 'flex-start' }}
          >
            {config.addButtonLabel ?? 'Add applicant'}
          </Button>
        ) : null}
      </Stack>
    </SectionShell>
  );
}
